import EmailInput from "@/app/components/emailInput";
import useNeoEmailSubscriber from "@/app/hooks/useNeoEmailSubscriber";
import fixImageURI from "@/app/ec/product/fixImageURI";
import Alert from "@/components/design-system/alert";
import Button from "@/components/design-system/button";
import Checkbox from "@/components/design-system/checkbox";
import Modal from "@/components/design-system/modal";
import { useGetUserQuery } from "@/store/userApi";
import axios from "axios";
import Image from "next/image";
import React, { FunctionComponent, useRef, useState } from "react";
import style from "./notifyMeModalPage.module.scss";

const NotifyMeModal: FunctionComponent<NotifyMeModalPageProps> = ({
  isVisible,
  mpId,
  alternativeMpIdList = [],
  mpName,
  mediaPath,
  url,
  descr,
  closeCallback,
}) => {
  const [displayErrorMessage, setDisplayErrorMessage] = useState(false);
  const [subscribeToEmails, setSubscribeToEmails] = useState(true);
  const [email, setEmail] = useState("");
  const notifyMeFormRef = useRef<HTMLFormElement>(null);
  const [formSubmitAttempted, setFormSubmitAttempted] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [submittingToBackend, setSubmittingToBackend] = useState(false);
  const { data: user } = useGetUserQuery();
  const subscribeToEmailLists = useNeoEmailSubscriber();

  const closeModal = () => {
    setFormSubmitAttempted(false);
    setEmail("");
    setEmailError(false);
    setDisplayErrorMessage(false);
    closeCallback(false);
  };

  const subscribeEmails = async () => {
    const response = await subscribeToEmailLists({
      emailAddress: email,
      subscriptionType: "Weekly Email",
      source: "oos-popup",
      isLoggedInUser: user?.IsGuestUser === false ? 1 : 0,
    });
    if (response instanceof Error) console.log("error ocurried", response);
  };

  const notifyMeBackendCall = async () => {
    setSubmittingToBackend(true);
    setDisplayErrorMessage(false);

    try {
      const alternativeMpIds = alternativeMpIdList ? alternativeMpIdList.join(",") : null;
      const postParams: NotifyMePostObjectType = {
        mpId: mpId,
        mpName: mpName,
        alternateMpIdList: alternativeMpIds ? alternativeMpIds : undefined,
        detailLink: url,
        mediaPath: mediaPath,
        email: email,
        descr: descr,
        webUrl: window.location.origin,
      };

      const { data: notifyMeResult } = await axios.post(
        "/rest/3pi-processing/subscribe-to-in-stock-notification",
        postParams,
      );

      switch (notifyMeResult) {
        case "SC_OK": {
          setEmail("");
          closeCallback(true);
          setFormSubmitAttempted(false);
          break;
        }
        default: {
          console.log("error ocurried", notifyMeResult);
          setDisplayErrorMessage(true);
        }
      }
      if (subscribeToEmails === true) subscribeEmails();

      setSubmittingToBackend(false);
    } catch (error) {
      console.log("notify for in stock error ocurried", error);
      setSubmittingToBackend(false);
      setDisplayErrorMessage(true);
    }
  };

  const onFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormSubmitAttempted(true);

    if (notifyMeFormRef.current?.checkValidity()) {
      setEmailError(false);
      notifyMeBackendCall();
    }
  };

  const onFormValidation = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormSubmitAttempted(true);
    setEmailError(true);
  };

  return (
    <Modal
      size="skinny"
      sizeDownMd="medium"
      show={isVisible}
      onHide={closeModal}
      title="Notify Me When Available"
      footer={
        <Button
          variant="primary"
          color="blue"
          size="large"
          text="Notify Me"
          showLeftIcon={false}
          showRightIcon={false}
          loading={submittingToBackend}
          disabled={submittingToBackend}
          onClick={() => notifyMeFormRef.current?.requestSubmit()}
        />
      }
    >
      <div className={style.content}>
        <div className={style.mediaTitle}>
          <Image
            alt={mpName}
            src={mediaPath ? fixImageURI(mediaPath) : ""}
            width={96}
            height={96}
            className={style.media}
          />
          <div className={style.title}>{mpName}</div>
        </div>

        <div className={style.emailDescription}>
          Enter your email address below to be the first to know when this product is back in stock.
        </div>

        <form
          ref={notifyMeFormRef}
          className={`${style.form} ${formSubmitAttempted ? "was-validated" : ""}`}
          onSubmit={onFormSubmit}
          onInvalid={onFormValidation}
        >
          <EmailInput
            name="email"
            required
            size="small"
            label="Email Address"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              setEmail(e.target.value.trim());
              if (emailError) setEmailError(false);
            }}
            error={emailError}
          />

          <Checkbox
            size="small"
            checked={subscribeToEmails}
            onChange={(event) => setSubscribeToEmails(event.target.checked)}
            label="Add me to your mailing list for updates"
          />
        </form>

        {displayErrorMessage && (
          <Alert
            variant="error"
            size="small"
            role="alert"
            message="An unknown error occurred. Please try again in a few moments."
          />
        )}
      </div>
    </Modal>
  );
};

export type NotifyMeModalPageProps = {
  mpId: number;
  mpName: string;
  isVisible: boolean;
  closeCallback: (isUserSubscribed: boolean) => void;
  notifyMeCallback?: (mpId: number, mpName: string | number) => void;
  /** This can sometimes be undefined and so it is marked as optional */
  alternativeMpIdList?: Array<string | number>;
  mediaPath: string;
  url: string;
  descr: string;
};

type NotifyMePostObjectType = {
  mpId: number;
  mpName: string;
  alternateMpIdList?: string;
  detailLink: string;
  mediaPath: string;
  email: string;
  descr: string;
  webUrl: string;
};

export default NotifyMeModal;
